import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Pencil, RotateCcw, RefreshCw } from 'lucide-react';
import api, { asList } from '../services/api';

const NAVY = '#1B3A5C';
const GREEN = '#43A047';
const FONT = "'Poppins', sans-serif";

interface Destination {
  code: string;
  kind: 'mobile' | 'bank';
  name: string;
  operators: string[];
  currency: string;
  fee_percent: number;
  fee_min: number;
  fee_max: number;
  is_active: boolean;
  custom: boolean;
  updated_by?: string;
}

interface Rule {
  fee_percent: number;
  fee_min: number;
  fee_max: number;
}

const card: React.CSSProperties = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: 20, fontFamily: FONT };
const btn = (bg = '#fff', color = NAVY): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: `1px solid ${bg === '#fff' ? '#D5DCE4' : bg}`,
  background: bg, color, fontFamily: FONT, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const input: React.CSSProperties = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #D5DCE4', fontFamily: FONT, fontSize: 13, boxSizing: 'border-box' };
const label: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: NAVY, margin: '12px 0 5px' };
const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11.5, fontWeight: 600, color: '#7C8D9E', borderBottom: '1px solid #E0E6ED', background: '#F8FAFB' };
const td: React.CSSProperties = { padding: '11px 12px', fontSize: 13, color: NAVY, borderBottom: '1px solid #F0F2F5', verticalAlign: 'middle' };

/** The fee the app charges: percent of the amount, clamped to [min, max]. Same rule as the backend. */
export function feeFor(amount: number, r: Rule): number {
  let fee = (amount * r.fee_percent) / 100;
  if (r.fee_min > 0 && fee < r.fee_min) fee = r.fee_min;
  if (r.fee_max > 0 && fee > r.fee_max) fee = r.fee_max;
  return Math.round(fee);
}

const xaf = (n: number) => `${new Intl.NumberFormat('en-US').format(Math.round(n))} XAF`;

export function flag(code: string): string {
  const c = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '🌍';
  return String.fromCodePoint(...Array.from(c).map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,20,30,0.5)', display: 'grid', placeItems: 'center', zIndex: 300, padding: 16 }}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ ...card, width: '100%', maxWidth: 440, maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ margin: '0 0 4px', color: NAVY, fontSize: 17 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

/** Fee form shared by the default fee and a destination's own fee. */
function FeeEditor({ initial, saving, onSave, onClose, saveLabel = 'Save' }: {
  initial: Rule; saving: boolean; onSave: (r: Rule) => void; onClose: () => void; saveLabel?: string;
}) {
  const [pct, setPct] = useState(String(initial.fee_percent));
  const [min, setMin] = useState(String(initial.fee_min));
  const [max, setMax] = useState(String(initial.fee_max));
  const rule: Rule = { fee_percent: parseFloat(pct) || 0, fee_min: parseFloat(min) || 0, fee_max: parseFloat(max) || 0 };
  const invalid = rule.fee_percent < 0 || rule.fee_percent > 25 || rule.fee_min < 0 || rule.fee_max < 0 || (rule.fee_max > 0 && rule.fee_min > rule.fee_max);

  return (
    <>
      <label style={label}>Fee (% of the amount sent)</label>
      <input style={input} type="number" min="0" max="25" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} aria-label="Fee percent" />
      <label style={label}>Minimum fee (XAF)</label>
      <input style={input} type="number" min="0" value={min} onChange={(e) => setMin(e.target.value)} aria-label="Minimum fee" />
      <label style={label}>Maximum fee (XAF) — 0 for no cap</label>
      <input style={input} type="number" min="0" value={max} onChange={(e) => setMax(e.target.value)} aria-label="Maximum fee" />
      <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: '#F4F8FB', fontSize: 12.5, color: NAVY }}>
        {invalid ? (
          <span style={{ color: '#C62828' }}>Fee must be 0–25%, and the minimum cannot exceed the maximum.</span>
        ) : (
          <>Example: sending {xaf(10000)} costs a fee of <b>{xaf(feeFor(10000, rule))}</b>; sending {xaf(200000)} costs <b>{xaf(feeFor(200000, rule))}</b>.</>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button style={btn()} onClick={onClose}>Cancel</button>
        <button style={btn(NAVY, '#fff')} disabled={invalid || saving} onClick={() => onSave(rule)}>{saving ? 'Saving…' : saveLabel}</button>
      </div>
    </>
  );
}

function Toggle({ on, onClick, label: aria }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={aria}
      onClick={onClick}
      style={{ width: 40, height: 22, borderRadius: 11, border: 'none', background: on ? GREEN : '#C5CED8', position: 'relative', cursor: 'pointer', padding: 0 }}
    >
      <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
    </button>
  );
}

export default function SendRates() {
  const [dests, setDests] = useState<Destination[]>([]);
  const [def, setDef] = useState<Rule>({ fee_percent: 0, fee_min: 0, fee_max: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Destination | 'default' | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listSendRates();
    setLoading(false);
    if (!res?.success) {
      setError(res?.message || 'Could not load the rates.');
      return;
    }
    setError('');
    const d = res.data && typeof res.data === 'object' ? res.data : {};
    setDests(asList<Destination>(d.destinations).map((x) => ({ ...x, operators: asList<string>(x.operators) })));
    if (d.default) setDef(d.default);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3500);
  };

  const save = async (d: Destination, rule: Rule, active: boolean) => {
    setSaving(true);
    const res = await api.updateSendRate(d.code, { ...rule, is_active: active });
    setSaving(false);
    if (!res?.success) {
      setError(res?.message || 'Could not save.');
      return;
    }
    setError('');
    setEditing(null);
    flash(`${d.name} saved.`);
    load();
  };

  const toggle = async (d: Destination) => {
    if (d.is_active && !window.confirm(`Switch off sending to ${d.name}? It disappears from the app straight away.`)) return;
    await save(d, d, !d.is_active);
  };

  const reset = async (d: Destination) => {
    if (!window.confirm(`Use the default fee for ${d.name}?`)) return;
    const res = await api.resetSendRate(d.code);
    if (!res?.success) setError(res?.message || 'Could not reset.');
    else flash(`${d.name} now uses the default fee.`);
    load();
  };

  const saveDefault = async (rule: Rule) => {
    setSaving(true);
    const res = await api.updateRate('withdrawal', rule);
    setSaving(false);
    if (!res?.success) {
      setError(res?.message || 'Could not save.');
      return;
    }
    setError('');
    setEditing(null);
    flash('Default fee saved.');
    load();
  };

  const q = search.trim().toLowerCase();
  const shown = useMemo(
    () => dests.filter((d) => !q || `${d.name} ${d.code} ${d.operators.join(' ')}`.toLowerCase().includes(q)),
    [dests, q],
  );

  const table = (kind: 'mobile' | 'bank', title: string, blurb: string) => {
    const rows = shown.filter((d) => d.kind === kind);
    return (
      <div style={{ ...card, padding: 0, marginTop: 18, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 6px' }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 12.5, color: '#7C8D9E', marginTop: 2 }}>{blurb}</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr>
                <th style={th}>Destination</th><th style={th}>Currency</th><th style={th}>Fee</th>
                <th style={th}>Min – max</th><th style={th}>Price</th><th style={th}>Available in app</th><th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.code} style={{ opacity: d.is_active ? 1 : 0.55 }}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{kind === 'mobile' ? flag(d.code) : '🏦'}</span>
                      <div>
                        <div style={{ fontWeight: 600 }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: '#7C8D9E' }}>{d.operators.join(', ')}</div>
                      </div>
                    </div>
                  </td>
                  <td style={td}>{d.currency}</td>
                  <td style={td}>{d.fee_percent}%</td>
                  <td style={td}>{xaf(d.fee_min)} – {d.fee_max > 0 ? xaf(d.fee_max) : 'no cap'}</td>
                  <td style={td}>
                    <span style={{ padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: d.custom ? '#E8F4EA' : '#EDF1F5', color: d.custom ? '#2E7D32' : '#5B6B7B' }}>
                      {d.custom ? 'Custom' : 'Default'}
                    </span>
                  </td>
                  <td style={td}><Toggle on={d.is_active} onClick={() => toggle(d)} label={`${d.name} available`} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    <button style={btn()} onClick={() => setEditing(d)} aria-label={`Edit ${d.name}`}><Pencil size={13} /> Set fee</button>{' '}
                    {d.custom && <button style={btn()} onClick={() => reset(d)} aria-label={`Reset ${d.name}`}><RotateCcw size={13} /> Reset</button>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td style={{ ...td, color: '#7C8D9E' }} colSpan={7}>{loading ? 'Loading…' : 'Nothing matches.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 32, fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: NAVY, margin: 0 }}>Send rates</h1>
          <p style={{ fontSize: 14, color: '#7C8D9E', margin: '4px 0 0' }}>
            What customers pay to send money abroad, per destination. Only countries and banks the app can reach are listed.
          </p>
        </div>
        <button style={btn()} onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>

      {error && <div role="alert" style={{ ...card, marginTop: 16, color: '#C62828', background: '#FDECEA' }}>{error}</div>}
      {notice && <div role="status" style={{ ...card, marginTop: 16, color: '#2E7D32', background: '#E8F4EA' }}>{notice}</div>}

      <div style={{ ...card, marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>Default fee</div>
          <div style={{ fontSize: 12.5, color: '#7C8D9E', marginTop: 2 }}>
            Used by every destination that has no price of its own. Currently <b>{def.fee_percent}%</b>, minimum {xaf(def.fee_min)}
            {def.fee_max > 0 ? `, maximum ${xaf(def.fee_max)}` : ', no cap'}.
          </div>
        </div>
        <button style={btn(NAVY, '#fff')} onClick={() => setEditing('default')}><Pencil size={13} /> Change default</button>
      </div>

      <div style={{ ...card, marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px' }}>
        <Search size={16} color="#7C8D9E" />
        <input
          style={{ border: 'none', outline: 'none', flex: 1, fontFamily: FONT, fontSize: 14 }}
          placeholder="Search a country, operator or bank…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search destinations"
        />
      </div>

      {table('mobile', 'Mobile money', 'MTN and Airtel in Congo-Brazzaville, and every country the app can pay out to. One price per country, shared by its operators.')}
      {table('bank', 'Bank transfers', 'Priced by transfer type (SEPA, USD wire, CFA zone…), not by country.')}

      <p style={{ fontSize: 12, color: '#7C8D9E', marginTop: 16 }}>
        The fee never drops below what the payment network itself charges us, so a very low price cannot make a send lose money.
        Merchants pay the network's cost only and are not affected by these prices.
      </p>

      {editing === 'default' && (
        <Modal title="Default fee" onClose={() => setEditing(null)}>
          <FeeEditor initial={def} saving={saving} onSave={saveDefault} onClose={() => setEditing(null)} />
        </Modal>
      )}
      {editing && editing !== 'default' && (
        <Modal title={`${editing.name} — fee`} onClose={() => setEditing(null)}>
          <FeeEditor
            initial={editing}
            saving={saving}
            saveLabel="Save price"
            onSave={(r) => save(editing, r, editing.is_active)}
            onClose={() => setEditing(null)}
          />
        </Modal>
      )}
    </div>
  );
}
